/**
 * The orientation tour (AIC-303; layout from the Codex prototype of 2026-09-29): three stops, each pointing at its
 * page's link in the Shell rail (Today, Assignments, Review), so no screen needs tour anchors of its own. The link
 * gets a blue outline; the card sits in the bottom-right corner (a bottom sheet on phones), clear of the rail.
 *
 * Nonmodal (plan review): the page stays usable, nothing is made inert and there is no showModal. The card is a
 * labelled, non-modal dialog region; focus moves to the step's description on every step, so a screen reader reads
 * it. Keys: Next and Back are buttons; Esc (inside the card, or anywhere on the page while no other dialog or menu is
 * open) exits and keeps the step for Resume. When the tour closes, focus goes back where it was before it opened.
 *
 * The tour explains; it never navigates, opens a record or acts. Its only controls are Skip / Back, Next / Finish and
 * Exit, so the user's route, filters and scroll stay as they were. A rail link that isn't on screen (removed, hidden
 * or not rendered yet) gets a short explanation instead of a broken pointer; the rail is watched, so a link that
 * comes back is pointed at again. Reduced motion: no animation and no smooth scrolling (useReducedMotion).
 */
import { createPortal } from "react-dom";
import { Link, useRoute } from "../router.tsx";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useReducedMotion } from "../components/motion.ts";
import { useToast } from "../components/Toast.tsx";
import { PRACTICE_LINK, TOUR_DONE, TOUR_FALLBACK, TOUR_STEPS } from "./content.ts";
import { useOnboarding } from "./OnboardingProvider.tsx";

/**
 * The rail link a step points at, when it is rendered and not hidden. A link inside a collapsed group (a closed
 * <details>, such as the Software Tracker group, AIC-404) isn't on screen either, so the step explains instead: the
 * tour never opens the group for the user.
 */
export function tourTarget(nav: string): HTMLElement | null {
  const el = document.querySelector<HTMLElement>(`.ipd-nav a[href="${nav}"]`);
  return el && !el.closest("[hidden]") && !insideClosedDetails(el) ? el : null;
}

/** True when a closed <details> hides `el`: it sits in the details' content, not in its always-visible summary. */
function insideClosedDetails(el: HTMLElement): boolean {
  for (let d = el.closest("details"); d; d = d.parentElement?.closest("details") ?? null) {
    if (!d.open && el.closest("summary")?.parentElement !== d) return true;
  }
  return false;
}

export function Tour() {
  const o = useOnboarding();
  const route = useRoute();
  const inline = route.path === "/help";
  const slot = inline ? o?.helpTourHost : null;
  const reduced = useReducedMotion();
  const toast = useToast();
  const id = useId();
  const desc = useRef<HTMLParagraphElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const [found, setFound] = useState(true);
  const run = o?.tour ?? null;
  const index = run ? TOUR_STEPS.findIndex((s) => s.id === run.step) : -1;
  const step = index >= 0 ? TOUR_STEPS[index]! : null;
  const open = run !== null;

  // Where focus was when the tour opened, to give it back on close; and room below the page on phones.
  useLayoutEffect(() => {
    if (!open) return;
    const before = document.activeElement;
    returnTo.current = before instanceof HTMLElement && before !== document.body ? before : null;
    return () => {
      const back = returnTo.current;
      returnTo.current = null;
      setTimeout(() => {
        const help = document.getElementById("help-tour-resume") ?? document.getElementById("gs-tour-h");
        if (help) help.focus({ preventScroll: true });
        else if (back?.isConnected && !back.closest("dialog:not([open])")) back.focus({ preventScroll: true });
        else document.getElementById("main")?.focus({ preventScroll: true });
      }, 0);
    };
  }, [open]);

  useEffect(() => {
    if (open && !inline) document.documentElement.classList.add("ob-touring");
    return () => document.documentElement.classList.remove("ob-touring");
  }, [open, inline]);

  // Ring the step's link, bring it into view, move focus to the description; re-point when the rail re-renders.
  useEffect(() => {
    if (!step) return;
    let ringed: HTMLElement | null = null;
    const point = () => {
      const target = tourTarget(step.nav);
      if (target !== ringed) { ringed?.classList.remove("ob-tour-target"); target?.classList.add("ob-tour-target"); ringed = target; }
      setFound(target !== null);
      return target;
    };
    point()?.scrollIntoView?.({ block: "nearest", inline: "center", behavior: reduced ? "auto" : "smooth" });
    desc.current?.focus({ preventScroll: true });
    const rail = document.querySelector(".ipd-rail");
    const mo = typeof MutationObserver === "undefined" || !rail ? null : new MutationObserver(() => { point(); });
    mo?.observe(rail!, { childList: true, subtree: true, attributes: true, attributeFilter: ["href", "hidden", "open"] });
    return () => { mo?.disconnect(); ringed?.classList.remove("ob-tour-target"); };
  }, [step, reduced, inline, slot]);

  // Esc on the page exits too, unless another dialog or the open All pages menu (phones) has it.
  useEffect(() => {
    if (!open || !o) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector("dialog[open]")) return;
      if (e.target instanceof Element && e.target.closest(".ipd-rail.is-open, dialog")) return;
      e.preventDefault();
      o.exitTour();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, o]);

  if (!o || !run || !step) return null;
  const first = index === 0;
  const last = index === TOUR_STEPS.length - 1;
  const finish = () => { o.completeTour(); toast.show({ tone: "ok", title: TOUR_DONE }); };
  if (slot) return createPortal(
    <section className="ob-inline-tour" aria-labelledby={`${id}-h`}>
      <div className="ob-inline-progress"><span>Orientation · Step {index + 1} of {TOUR_STEPS.length}</span><span>{step.id[0]!.toUpperCase() + step.id.slice(1)} in the workspace menu</span></div>
      <h2 id={`${id}-h`}>{step.title}</h2>
      <p ref={desc} tabIndex={-1}>{step.body}</p>
      {!found ? <p className="ob-inline-note" role="status">{TOUR_FALLBACK}</p> : null}
      <p className="ob-inline-note">This tour explains the workspace; it does not open pages or act on surveys.</p>
      <div className="ob-acts">
        <button className="ipd-btn" disabled={first} onClick={() => o.goToStep(TOUR_STEPS[index - 1]!.id)}>Previous</button>
        <button className="ipd-btn ipd-btn--go" onClick={last ? finish : () => o.goToStep(TOUR_STEPS[index + 1]!.id)}>{last ? "Finish tour" : "Next step"}</button>
        {last ? <Link className="ipd-btn" to={PRACTICE_LINK.to} onClick={finish}>{PRACTICE_LINK.label}</Link> : null}
        <button className="ipd-btn" onClick={() => o.exitTour()}>Pause tour</button>
      </div>
    </section>, slot);
  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-labelledby={`${id}-h`}
      aria-describedby={`${id}-p`}
      className={`ob-tour${reduced ? " is-still" : ""}`}
    >
      <div className="ob-tour-top">
        <p className="ob-tour-kicker">Getting started · {index + 1} of {TOUR_STEPS.length}</p>
        <button type="button" className="ob-tour-quiet" onClick={() => o.exitTour()}>Exit tour</button>
      </div>
      <h2 id={`${id}-h`}>{step.title}</h2>
      <p id={`${id}-p`} ref={desc} tabIndex={-1} className="ob-tour-desc">{step.body}</p>
      {!found ? <p className="ob-tour-fallback" role="status">{TOUR_FALLBACK}</p> : null}
      <div className="ob-tour-acts">
        {first
          ? <button type="button" className="ob-tour-quiet" onClick={() => o.exitTour()}>Skip for now</button>
          : <button type="button" className="ob-tour-quiet" onClick={() => o.goToStep(TOUR_STEPS[index - 1]!.id)}>Back</button>}
        {last
          ? <>
              <Link className="ob-tour-quiet" to={PRACTICE_LINK.to} onClick={finish}>{PRACTICE_LINK.label}</Link>
              <button type="button" className="ipd-btn ipd-btn--go" onClick={finish}>Finish tour<span aria-hidden="true"> ✓</span></button>
            </>
          : <button type="button" className="ipd-btn ipd-btn--go" onClick={() => o.goToStep(TOUR_STEPS[index + 1]!.id)}>Next<span aria-hidden="true"> →</span></button>}
      </div>
    </section>
  );
}
